import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { QrCode, Printer, Package, Search, CheckSquare, Square, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Avery formats (mm) — standards français
const LAYOUTS = [
  { id: "avery-l7159", name: "Avery L7159 — 24 par page (63.5×33.9 mm)", cols: 3, rows: 8, w: 63.5, h: 33.9, mt: 13.3, ml: 7.0, gap: 2.5 },
  { id: "avery-l7160", name: "Avery L7160 — 21 par page (63.5×38.1 mm)", cols: 3, rows: 7, w: 63.5, h: 38.1, mt: 15.2, ml: 7.2, gap: 2.5 },
  { id: "avery-l7163", name: "Avery L7163 — 14 par page (99.1×38.1 mm)", cols: 2, rows: 7, w: 99.1, h: 38.1, mt: 15.2, ml: 4.7, gap: 2.5 },
  { id: "avery-l7165", name: "Avery L7165 — 8 par page (99.1×67.7 mm)", cols: 2, rows: 4, w: 99.1, h: 67.7, mt: 13.0, ml: 4.7, gap: 2.5 },
  { id: "avery-l7167", name: "Avery L7167 — 1 par page (199.6×289.1 mm)", cols: 1, rows: 1, w: 199.6, h: 289.1, mt: 4, ml: 4.5, gap: 0 },
  { id: "grid-6", name: "Grille 3×2 simple — 6 étiquettes", cols: 3, rows: 2, w: 63.5, h: 100, mt: 15, ml: 7, gap: 4 },
];

export default function QrLabelsAdmin() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({}); // { productId: count }
  const [layoutId, setLayoutId] = useState("avery-l7160");
  const [showName, setShowName] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showSku, setShowSku] = useState(false);
  const [baseUrl, setBaseUrl] = useState("");
  const [copied, setCopied] = useState("");

  useEffect(() => {
    (async () => {
      const [p, c, s] = await Promise.all([
        api.get("/products"),
        api.get("/product-categories/all").catch(() => ({ data: [] })),
        api.get("/settings"),
      ]);
      setProducts(p.data || []);
      setCategories(c.data || []);
      setBaseUrl(s.data?.canonical_base || s.data?.canonical_url || window.location.origin);

      // Pré-sélection via ?product=slug (venant de ProductsAdmin)
      const paramSlug = searchParams.get("product");
      if (paramSlug) {
        const match = (p.data || []).find(pp => pp.slug === paramSlug);
        if (match) {
          setSelected({ [match.id]: 1 });
          setSearch(match.name);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const layout = LAYOUTS.find(l => l.id === layoutId) || LAYOUTS[0];
  const maxPerPage = layout.cols * layout.rows;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q) || p.slug?.toLowerCase().includes(q)
    );
  }, [products, search]);

  // Flat list of labels to render (one entry per label, duplicated by count)
  const labels = useMemo(() => {
    const out = [];
    for (const [pid, count] of Object.entries(selected)) {
      const prod = products.find(p => p.id === pid);
      if (!prod || count <= 0) continue;
      for (let i = 0; i < count; i++) out.push(prod);
    }
    return out;
  }, [selected, products]);

  const pages = Math.ceil(labels.length / maxPerPage) || 0;
  const totalSelected = Object.values(selected).reduce((s, n) => s + (n || 0), 0);

  const toggleOne = (pid, delta = 1) => {
    setSelected(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) + delta) }));
  };
  const setQty = (pid, v) => {
    setSelected(prev => ({ ...prev, [pid]: Math.max(0, Math.min(200, parseInt(v, 10) || 0)) }));
  };
  const selectAll = (qty = 1) => {
    const next = {};
    for (const p of filtered) next[p.id] = qty;
    setSelected(next);
  };
  const clear = () => setSelected({});

  const productUrl = (p) => `${(baseUrl || "").replace(/\/$/, "")}/shop/${p.slug}`;

  const copyUrl = async (p) => {
    try { await navigator.clipboard.writeText(productUrl(p)); setCopied(p.id); setTimeout(() => setCopied(""), 1500); } catch {}
  };

  const printSheet = () => {
    if (labels.length === 0) { toast.error("Sélectionnez au moins un produit"); return; }
    window.print();
  };

  return (
    <div className="p-6 md:p-10 space-y-5 print:p-0" data-testid="admin-qr-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 print:hidden">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Boutique · Impression</div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">QR codes produits</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Générez une planche A4 imprimable pour coller un QR code sur vos produits (en rayon, en stock, sur devis…). Chaque QR pointe vers la fiche produit publique.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={clear} data-testid="qr-clear-btn">Effacer la sélection</Button>
          <Button onClick={printSheet} disabled={labels.length === 0} className="bg-[color:var(--brand-red)]" data-testid="qr-print-btn">
            <Printer className="w-4 h-4 mr-1.5" />Imprimer ({labels.length})
          </Button>
        </div>
      </header>

      {/* Config */}
      <Card className="p-5 border space-y-4 print:hidden" data-testid="qr-config-card">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="flex items-center gap-1"><QrCode className="w-3.5 h-3.5" />Format / planche</Label>
            <Select value={layoutId} onValueChange={setLayoutId}>
              <SelectTrigger data-testid="qr-layout-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LAYOUTS.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">{layout.cols} × {layout.rows} = <b>{maxPerPage}</b> étiquettes par A4.</p>
          </div>
          <div>
            <Label>URL de base des fiches</Label>
            <Input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} placeholder="https://votre-site.fr" data-testid="qr-base-url" />
          </div>
          <div className="flex flex-col justify-end gap-1">
            <div className="flex items-center gap-2"><Switch checked={showName} onCheckedChange={setShowName} /><span className="text-sm">Afficher le nom</span></div>
            <div className="flex items-center gap-2"><Switch checked={showPrice} onCheckedChange={setShowPrice} /><span className="text-sm">Afficher le prix</span></div>
            <div className="flex items-center gap-2"><Switch checked={showSku} onCheckedChange={setShowSku} /><span className="text-sm">Afficher la référence (SKU)</span></div>
          </div>
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2">
          <b className="text-[color:var(--brand-navy)]">{totalSelected}</b> étiquettes sélectionnées · <b className="text-[color:var(--brand-navy)]">{pages}</b> page(s) A4
        </div>
      </Card>

      {/* Products picker */}
      <Card className="p-5 border print:hidden" data-testid="qr-products-card">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom, SKU, slug…" className="flex-1" data-testid="qr-search" />
          <Button variant="outline" size="sm" onClick={() => selectAll(1)} data-testid="qr-select-all-1">Tout · 1</Button>
          <Button variant="outline" size="sm" onClick={() => selectAll(2)}>Tout · 2</Button>
        </div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]"><Package className="w-8 h-8 mx-auto mb-2 opacity-30" />Aucun produit</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {filtered.map(p => {
              const qty = selected[p.id] || 0;
              const active = qty > 0;
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-all ${active ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5" : "border-[hsl(var(--border))]"}`}
                  data-testid={`qr-product-${p.slug}`}
                >
                  <button type="button" onClick={() => toggleOne(p.id, active ? -qty : 1)} className="shrink-0 text-[color:var(--brand-sky)]">
                    {active ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-[hsl(var(--muted-foreground))]/50" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[color:var(--brand-navy)] truncate">{p.name}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))] truncate font-mono">
                      {p.sku || p.slug}
                      <button onClick={() => copyUrl(p)} className="ml-1.5 inline-flex items-center gap-0.5 hover:text-[color:var(--brand-sky)]" title="Copier l'URL du QR">
                        {copied === p.id ? <Check className="w-2.5 h-2.5 text-emerald-500" /> : <Copy className="w-2.5 h-2.5" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button type="button" onClick={() => toggleOne(p.id, -1)} disabled={qty === 0} className="w-6 h-6 rounded border text-sm disabled:opacity-30">−</button>
                    <Input type="number" value={qty} onChange={e => setQty(p.id, e.target.value)} min={0} max={200} className="h-6 w-12 text-center text-xs px-1" />
                    <button type="button" onClick={() => toggleOne(p.id, 1)} className="w-6 h-6 rounded border text-sm">+</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Print preview */}
      <div className="bg-[hsl(var(--muted))]/30 p-6 rounded-xl print:p-0 print:bg-white" data-testid="qr-preview">
        <div className="text-center text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-3 print:hidden">
          Aperçu A4 (210×297 mm) — {pages} page(s)
        </div>
        {labels.length === 0 ? (
          <div className="bg-white mx-auto max-w-2xl aspect-[210/297] flex items-center justify-center text-sm text-[hsl(var(--muted-foreground))] border shadow print:hidden">
            Sélectionnez des produits pour générer la planche
          </div>
        ) : (
          Array.from({ length: pages }).map((_, pageIdx) => (
            <A4Page key={pageIdx} labels={labels.slice(pageIdx * maxPerPage, (pageIdx + 1) * maxPerPage)}
              layout={layout} showName={showName} showPrice={showPrice} showSku={showSku} baseUrl={baseUrl} productUrl={productUrl} pageIdx={pageIdx} />
          ))
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .admin-sidebar, nav[role=navigation], [data-testid='admin-sidebar'] { display: none !important; }
          header { display: none !important; }
          .qr-a4-page { page-break-after: always; margin: 0 !important; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
}

function A4Page({ labels, layout, showName, showPrice, showSku, productUrl, pageIdx }) {
  // Convert mm to CSS with a scale factor; 1mm = 3.7795px at 96dpi
  const mm = (v) => `${v}mm`;
  return (
    <div
      className="qr-a4-page bg-white mx-auto shadow print:shadow-none border print:border-0"
      style={{
        width: "210mm",
        height: "297mm",
        position: "relative",
        marginBottom: "18mm",
      }}
      data-testid={`qr-page-${pageIdx}`}
    >
      <div
        style={{
          position: "absolute",
          top: mm(layout.mt),
          left: mm(layout.ml),
          display: "grid",
          gridTemplateColumns: `repeat(${layout.cols}, ${mm(layout.w)})`,
          gridAutoRows: mm(layout.h),
          columnGap: mm(layout.gap),
          rowGap: mm(layout.gap),
        }}
      >
        {labels.map((p, idx) => (
          <LabelCell key={idx} p={p} w={layout.w} h={layout.h} showName={showName} showPrice={showPrice} showSku={showSku} productUrl={productUrl} />
        ))}
      </div>
    </div>
  );
}

function LabelCell({ p, w, h, showName, showPrice, showSku, productUrl }) {
  // mm → px : 1mm = 96/25.4 ≈ 3.78 px (at 96 DPI). Use the smallest dimension minus padding.
  // The QR is square and must keep its quiet zone (includeMargin=true) to be scannable.
  const MM_TO_PX = 96 / 25.4;
  const padding = 2; // 2mm of inner padding
  const available = Math.max(10, Math.min(w, h) - padding * 2);
  const qrSize = Math.round(available * MM_TO_PX);
  return (
    <div
      className="flex items-center gap-2 overflow-hidden"
      style={{ width: `${w}mm`, height: `${h}mm`, border: "0.5px dashed rgba(0,0,0,0.15)", padding: `${padding}mm` }}
    >
      <div className="shrink-0 flex items-center justify-center" style={{ width: `${available}mm`, height: `${available}mm` }}>
        <QRCodeCanvas value={productUrl(p)} size={qrSize} level="M" includeMargin={true} />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center" style={{ fontSize: h < 40 ? "7pt" : "9pt", lineHeight: 1.2 }}>
        {showName && <div className="font-bold truncate" style={{ fontSize: h < 40 ? "8pt" : "10pt" }}>{p.name}</div>}
        {showPrice && p.price_cents > 0 && <div className="font-bold" style={{ color: "#d43b57", fontSize: h < 40 ? "9pt" : "11pt" }}>{(p.price_cents / 100).toFixed(2)} €</div>}
        {showSku && (p.sku || p.slug) && <div className="font-mono text-[6pt] truncate opacity-70">{p.sku || p.slug}</div>}
      </div>
    </div>
  );
}
